import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Project } from '../types';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import { getProjectBySlug, getRelatedProjects } from '../data/projectsData';

const ProjectDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [relatedProjects, setRelatedProjects] = useState<Project[]>([]);

  useEffect(() => {
    // Simulate API call delay
    const timer = setTimeout(() => {
      if (slug) {
        const foundProject = getProjectBySlug(slug);
        
        if (foundProject) {
          setProject(foundProject);
          const related = getRelatedProjects(foundProject);
          setRelatedProjects(related);
        }
      }
      setLoading(false);
    }, 500); // Small delay for better UX

    return () => clearTimeout(timer);
  }, [slug]);

  if (loading) {
    return <LoadingSpinner text="Loading project details..." />;
  }

  if (!project) {
    return (
      <div className="container text-center py-5" style={{ marginTop: '76px' }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="display-1 text-gray-400 mb-4">📄</div>
          <h2 className="fw-bold text-gray-800 mb-3">Project Not Found</h2>
          <p className="text-gray-600 mb-4">
            The project you're looking for doesn't exist. Please check the URL or return to the programs page.
          </p>
          <Link to="/programs" className="btn btn-primary btn-lg">
            ← Back to All Programs
          </Link>
        </motion.div>
      </div>
    );
  }

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'food_security': return '🌱';
      case 'climate_action': return '🌍';
      case 'youth_empowerment': return '👨‍🎓';
      case 'women_empowerment': return '👩‍🌾';
      default: return '📋';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'primary';
      case 'completed': return 'gray-600';
      case 'upcoming': return 'warning';
      case 'planning': return 'gray-500';
      default: return 'primary';
    }
  };

  return (
    <>
      <Helmet>
        <title>{project.title} - Impact Generation Africa</title>
        <meta name="description" content={project.description} />
      </Helmet>

      {/* Hero Section */}
      <section 
        className="py-5 text-white position-relative"
        style={{ 
          background: 'linear-gradient(135deg, rgba(74, 85, 104, 0.9) 0%, rgba(45, 55, 72, 0.95) 100%)',
          marginTop: '76px'
        }}
      >
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-8">
              
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="d-flex align-items-center mb-3">
                  <span className="fs-2 me-3">{getCategoryIcon(project.category)}</span>
                  <span className={`badge bg-${getStatusColor(project.status)} me-3`}>
                    {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
                  </span>
                  <span className="text-gray-300 text-capitalize">
                    {project.category.replace('_', ' ')}
                  </span>
                </div>
                
                <h1 className="display-5 fw-bold mb-4">{project.title}</h1>
                <p className="lead mb-4 opacity-90">{project.description}</p>
                
                <div className="d-flex flex-wrap gap-3">
                  {project.location && (
                    <div className="bg-white bg-opacity-10 rounded-pill px-3 py-1">
                      <small>📍 {project.location}</small>
                    </div>
                  )}
                  {project.duration && (
                    <div className="bg-white bg-opacity-10 rounded-pill px-3 py-1">
                      <small>⏱️ {project.duration}</small>
                    </div>
                  )}
                  {project.impact && (
                    <div className="bg-white bg-opacity-10 rounded-pill px-3 py-1">
                      <small>💫 {project.impact}</small>
                    </div>
                  )}
                </div>
              </motion.div>
            </div>
            
            <div className="col-lg-4 text-center">
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                {/* Progress Circle */}
                <div className="position-relative d-inline-block">
                  <svg width="120" height="120" className="rotate-90">
                    <circle
                      cx="60"
                      cy="60"
                      r="54"
                      fill="none"
                      stroke="rgba(255,255,255,0.3)"
                      strokeWidth="8"
                    />
                    <circle
                      cx="60"
                      cy="60"
                      r="54"
                      fill="none"
                      stroke="white"
                      strokeWidth="8"
                      strokeDasharray={`${project.progress * 3.39} 339`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="position-absolute top-50 start-50 translate-middle text-center">
                    <div className="fw-bold fs-3">{project.progress}%</div>
                    <div className="small opacity-80">Complete</div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Project Details */}
      <section className="py-5 bg-gray-50">
        <div className="container">
          <div className="row">
            {/* Main Content */}
            <div className="col-lg-8">
              {/* Project Image */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="mb-5"
              >
                <img
                  src={project.image}
                  alt={project.title}
                  className="img-fluid rounded-4 w-100 shadow-sm border"
                  style={{ height: '400px', objectFit: 'cover' }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = '/images/about-1.jpeg'; // local fallback
                  }}
                />
              </motion.div>

              {/* Project Content */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="mb-5"
              >
                <h3 className="fw-bold mb-4 text-gray-800">About This Project</h3>
                <div 
                  className="project-content text-gray-700"
                  style={{ lineHeight: '1.8', fontSize: '1.1rem' }}
                >
                  {project.full_content ? (
                    <div dangerouslySetInnerHTML={{ 
                      __html: project.full_content 
                    }} />
                  ) : (
                    <p className="text-gray-600">
                      {project.description}
                    </p>
                  )}
                </div>
              </motion.div>

              {/* Project Goals */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="mb-5"
              >
                <h4 className="fw-bold mb-3 text-gray-800">Project Goals</h4>
                <div className="bg-white rounded-4 p-4 border">
                  <p className="mb-0 text-gray-700" style={{ lineHeight: '1.6' }}>
                    {project.goals}
                  </p>
                </div>
              </motion.div>

              {/* Additional Project Information */}
              {(project.challenges || project.success_stories || project.partner_organizations) && (
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                  className="mb-5"
                >
                  <h4 className="fw-bold mb-4 text-gray-800">Additional Information</h4>
                  <div className="row g-4">
                    {project.challenges && (
                      <div className="col-md-6">
                        <div className="bg-gray-100 rounded-4 p-4 h-100 border">
                          <h6 className="fw-bold text-gray-700 mb-3">🚧 Challenges</h6>
                          <p className="mb-0 text-gray-600" style={{ lineHeight: '1.6' }}>{project.challenges}</p>
                        </div>
                      </div>
                    )}
                    {project.success_stories && (
                      <div className="col-md-6">
                        <div className="bg-gray-100 rounded-4 p-4 h-100 border">
                          <h6 className="fw-bold text-gray-700 mb-3">🌟 Success Stories</h6>
                          <p className="mb-0 text-gray-600" style={{ lineHeight: '1.6' }}>{project.success_stories}</p>
                        </div>
                      </div>
                    )}
                    {project.partner_organizations && (
                      <div className="col-12">
                        <div className="bg-gray-100 rounded-4 p-4 border">
                          <h6 className="fw-bold text-gray-700 mb-3">🤝 Partners</h6>
                          <p className="mb-0 text-gray-600" style={{ lineHeight: '1.6' }}>{project.partner_organizations}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </div>

            {/* Sidebar */}
            <div className="col-lg-4">
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                {/* Project Stats */}
                <div className="card border-0 shadow-sm mb-4 bg-white">
                  <div className="card-body">
                    <h5 className="card-title fw-bold mb-4 text-gray-800">Project Details</h5>
                    
                    <div className="mb-3">
                      <small className="text-gray-600 d-block">Status</small>
                      <div className="d-flex align-items-center">
                        <span className={`badge bg-${getStatusColor(project.status)} me-2`}>
                          {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
                        </span>
                        <span className="text-gray-700">{project.progress}% Complete</span>
                      </div>
                    </div>

                    {project.location && (
                      <div className="mb-3">
                        <small className="text-gray-600 d-block">Location</small>
                        <span className="text-gray-700">📍 {project.location}</span>
                      </div>
                    )}

                    {project.duration && (
                      <div className="mb-3">
                        <small className="text-gray-600 d-block">Duration</small>
                        <span className="text-gray-700">⏱️ {project.duration}</span>
                      </div>
                    )}

                    {project.impact && (
                      <div className="mb-3">
                        <small className="text-gray-600 d-block">Impact</small>
                        <span className="text-gray-700">💫 {project.impact}</span>
                      </div>
                    )}

                    {project.volunteers_needed && (
                      <div className="mb-3">
                        <small className="text-gray-600 d-block">Volunteers Needed</small>
                        <span className="text-gray-700">👥 {project.volunteers_needed} people</span>
                      </div>
                    )}

                    {project.budget && (
                      <div className="mb-3">
                        <small className="text-gray-600 d-block">Budget</small>
                        <span className="text-gray-700">💰 ${project.budget.toLocaleString()}</span>
                      </div>
                    )}

                    <div className="mt-4">
                      <Link to="/donate" className="btn btn-primary w-100 mb-2">
                        💚 Support This Project
                      </Link>
                      <Link to="/volunteer" className="btn btn-outline-gray-700 w-100 border">
                        🤝 Volunteer
                      </Link>
                    </div>
                  </div>
                </div>

                {/* Related Projects */}
                {relatedProjects.length > 0 && (
                  <div className="card border-0 shadow-sm bg-white">
                    <div className="card-body">
                      <h5 className="card-title fw-bold mb-4 text-gray-800">Related Projects</h5>
                      {relatedProjects.map((relatedProject) => (
                        <Link
                          key={relatedProject.id}
                          to={`/programs/${relatedProject.slug}`}
                          className="d-block text-decoration-none text-gray-800 mb-3 p-3 rounded-3 bg-gray-50-hover border-hover"
                        >
                          <div className="d-flex align-items-center">
                            <span className="fs-5 me-3">
                              {getCategoryIcon(relatedProject.category)}
                            </span>
                            <div>
                              <h6 className="mb-1 fw-semibold">{relatedProject.title}</h6>
                              <small className="text-gray-600 text-capitalize">
                                {relatedProject.category.replace('_', ' ')}
                              </small>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        .bg-gray-50-hover:hover {
          background-color: #f8fafc !important;
          transform: translateY(-2px);
          transition: all 0.3s ease;
        }
        
        .border-hover:hover {
          border: 1px solid #e2e8f0 !important;
        }
        
        .rotate-90 {
          transform: rotate(-90deg);
        }
        
        .project-content h4 {
          color: #4a5568;
          margin: 1.5rem 0 1rem 0;
          font-weight: 600;
        }
        
        /* Custom badge colors */
        .bg-primary {
          background: linear-gradient(135deg, #48bb78 0%, #38a169 100%) !important;
        }
        
        .bg-gray-600 {
          background-color: #4b5563 !important;
        }
        
        .bg-gray-500 {
          background-color: #6b7280 !important;
        }
        
        .btn-primary {
          background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
          border: none;
          color: white;
        }
        
        .btn-outline-gray-700 {
          border-color: #4a5568;
          color: #4a5568;
          background: transparent;
        }
        
        .btn-outline-gray-700:hover {
          background: #4a5568;
          color: white;
        }
        
        /* Reduced motion support */
        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>
    </>
  );
};

export default ProjectDetail;